<template>
    <div class="register-form-wrap">
        <div class="register-form">
            <h1 class="register-title">{{ $t('entrance.create_user') }}</h1>

            <v-form v-model="valid">
                <kimsa-text-field
                    :label="$t('entrance.email')"
                    :placeholder="$t('entrance.email_placeholder')"
                    :rules="emailRules"
                    :value="email"
                    @change="email = $event"
                    @blur="checkEmailSuggestion"
                    :inputMode="'email'"
                />

                <v-chip v-if="emailSuggestion" small outlined color="error" class="mt-2"
                        close close-icon="mdi-close"
                        @click="applyEmailSuggestion"
                        @click:close="dismissEmailSuggestion">
                    {{ $t('entrance.email_suggestion', {email: emailSuggestion}) }}
                </v-chip>

                <kimsa-password-field
                    class="mt-4"
                    :label="$t('entrance.password')"
                    :placeholder="$t('entrance.password_placeholder')"
                    :rules="passwordRules"
                    @change="password = $event"
                    @enter="signUp"
                />

                <v-btn color="primary" depressed block large
                       class="black--text text-none font-weight-bold mt-6"
                       :loading="loadingSignUp"
                       @click="signUp">
                    {{ $t('common.confirm') }}
                </v-btn>

                <deleted-account-dialog ref="deletedDialog"/>
            </v-form>
        </div>
    </div>
</template>

<script>

import KimsaTextField from "../common/inputs/KimsaTextField";
import KimsaPasswordField from "../common/inputs/KimsaPasswordField";
import DeletedAccountDialog from "@/components/entrance/DeletedAccountDialog";

import {mapMutations} from 'vuex'

import firebase from 'firebase/app'
import "firebase/auth";
import {db} from "@/utils/Firebase";
import {sendVerificationEmail} from "@/utils/EmailVerification";
import {checkDeletedAccount} from "@/utils/DeletedAccount";
import {suggestEmail} from "@/utils/EmailSuggestion";

const ACCOUNT_TYPES = ['student', 'coach', 'company']

export default {
    data(vm = this) {
        return {
            valid: false,
            email: '',
            emailSuggestion: null,
            dismissedEmail: null,
            password: '',
            loadingSignUp: false,
            emailRules: [
                v => !!v || vm.$t('rules.required'),
                v => {
                    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                    return pattern.test(v) || vm.$t('rules.invalid_email')
                }
            ],
            passwordRules: [
                v => !!v || vm.$t('rules.required'),
                v => v && v.length >= 6 || vm.$t('rules.min_length', {num: '6'})
            ],
        }
    },
    components: {
        KimsaTextField,
        KimsaPasswordField,
        DeletedAccountDialog
    },
    methods: {
        checkEmailSuggestion() {
            const vm = this
            if (vm.email === vm.dismissedEmail) return
            vm.emailSuggestion = suggestEmail(vm.email)
        },
        applyEmailSuggestion() {
            const vm = this
            vm.email = vm.emailSuggestion
            vm.emailSuggestion = null
        },
        dismissEmailSuggestion() {
            const vm = this
            vm.dismissedEmail = vm.email
            vm.emailSuggestion = null
        },
        ...mapMutations(['showSnackBar']),
        async signUp() {
            const vm = this

            if (!vm.valid) return vm.showSnackBar({
                text: vm.$t('entrance.wrong_data'),
                color: 'error'
            })
            vm.loadingSignUp = true

            await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL)
                .then(() => {
                    firebase.auth().createUserWithEmailAndPassword(vm.email, vm.password)
                        .then(async res => {
                            const currentUser = firebase.auth().currentUser
                            let payload = {
                                email: currentUser.email,
                                rating: {count: 0, stars: 0},
                                language: vm.$i18n.locale
                            }
                            const accountType = String(vm.$route?.query?.type || '').trim().toLowerCase()
                            if (ACCOUNT_TYPES.includes(accountType)) payload.accountType = accountType
                            await db.collection('users').doc(currentUser.uid).set(payload, {merge: true})
                            try {
                                await sendVerificationEmail(vm.$i18n.locale)
                            } catch (err) {
                                console.log('sendVerificationEmail error:', err && err.message)
                            }
                            await vm.$store.dispatch('initUser', currentUser)
                            vm.$emit('success', currentUser)
                        })
                        .catch(error => vm.handleSignUpError(error))
                        .finally(function () {
                            vm.loadingSignUp = false
                        });
                })
                .catch(error => {
                    vm.loadingSignUp = false
                    vm.handleSignUpError(error)
                })
        },
        async handleSignUpError(error) {
            const vm = this
            const errorCode = error && error.code

            if (errorCode === 'auth/email-already-in-use') {
                const info = await checkDeletedAccount(vm.email)
                if (info) return vm.$refs.deletedDialog?.show(info, 'signup')
            }

            const message = errorCode === 'auth/email-already-in-use' ?
                vm.$t('entrance.mail_already_exists') :
                vm.$t('entrance.error_creating_account')

            vm.showSnackBar({text: message, color: 'error'})
        },
    },
}
</script>

<style scoped>

.register-form-wrap {
    width: 100%;
    max-width: 380px;
}

.register-form {
    width: 100%;
}

.register-title {
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin-bottom: 24px;
}

</style>
